import { RadioButton } from "@newsela/angelou";
import { v4 } from "uuid";

import { INPUT_MAX_LENGTH } from "@/components/AIComponents/ExportablesModal";
import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import OrganizerDirections from "@/components/AIComponents/ExportablesModal/templates/components/OrganizerDirections";
import {
  containerStyles,
  guideCellReason,
  guideCellStatement,
  guideHeaderCellReason,
  guideHeaderCellStatement,
  guideHeaderCellYN,
  guideHeaderRow,
  guideRow,
  guideRowsContainer,
  paragraphCss,
  radioButtonCss,
  textareaCss,
  ynCellCss,
} from "./AnticipationGuideOrganizer.styles";

import type { TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";

interface AnticipationGuideFormData {
  articleShortTitle?: string | undefined;
  article_title?: string;
  directions?: string;
  statements: string[];
  beforeReasons?: string[];
  afterReasons?: string[];
  beforeReading?: ("Y" | "N" | "")[];
  afterReading?: ("Y" | "N" | "")[];
  topic?: string;
}

// Type guard to check if formData is AnticipationGuideFormData
const isAnticipationGuideFormData = (
  data: any,
): data is AnticipationGuideFormData => {
  return (
    data &&
    typeof data === "object" &&
    ("statements" in data ||
      "beforeReasons" in data ||
      "afterReasons" in data ||
      "beforeReading" in data ||
      "afterReading" in data ||
      "topic" in data ||
      "articleShortTitle" in data)
  );
};

const MAX_ROWS = 4;

const AnticipationGuideOrganizer = ({
  formData,
  onChange,
  title = "Anticipation guide",
  isStudent = false,
}: TemplateProps<AnticipationGuideFormData>) => {
  // Ensure formData is treated as AnticipationGuideFormData
  const anticipationData = isAnticipationGuideFormData(formData)
    ? formData
    : ({} as AnticipationGuideFormData);

  const articleTitle =
    anticipationData?.article_title ||
    anticipationData?.articleShortTitle ||
    "";

  const quotedArticleTitle =
    articleTitle.trim() !== "" ? `, "${articleTitle}"` : "";

  const directionsText = `Read each statement about ${anticipationData?.topic || "topic"} carefully. If you agree with the statement, mark "Y" in the "Before Reading" column. If you disagree, mark "N" in the "Before Reading" column. Give a reason in the "Reason" column. Then, read the article ${quotedArticleTitle}. After reading the article, respond to the statements again in the "After Reading" column and note if any of your positions changed!`;

  const { directions = directionsText } = anticipationData;

  const statements = anticipationData?.statements ?? [];
  const beforeReasons = anticipationData?.beforeReasons ?? [];
  const afterReasons = anticipationData?.afterReasons ?? [];
  const beforeReading = anticipationData?.beforeReading ?? [];
  const afterReading = anticipationData?.afterReading ?? [];

  // Pad arrays to MAX_ROWS for display
  const paddedStatements = Array.from({ length: MAX_ROWS }).map(
    (_, i) => statements[i] ?? "",
  );
  const paddedBeforeReasons = Array.from({ length: MAX_ROWS }).map(
    (_, i) => beforeReasons[i] ?? "",
  );
  const paddedAfterReasons = Array.from({ length: MAX_ROWS }).map(
    (_, i) => afterReasons[i] ?? "",
  );
  const paddedBeforeReading = Array.from({ length: MAX_ROWS }).map(
    (_, i) => beforeReading[i] ?? "",
  );
  const paddedAfterReading = Array.from({ length: MAX_ROWS }).map(
    (_, i) => afterReading[i] ?? "",
  );

  const handleChange = (update: Partial<AnticipationGuideFormData>) => {
    if (!onChange) return;
    onChange(update);
  };

  const ynCell = (isBefore: boolean, idx: number) => {
    const options = [
      { value: "Y" as const, label: "Y", fullLabel: "yes" },
      { value: "N" as const, label: "N", fullLabel: "no" },
    ];

    return (
      <div css={ynCellCss} role="radiogroup">
        {options.map(({ value, label, fullLabel }) => (
          <RadioButton
            key={value}
            __cssFor={{ root: radioButtonCss }}
            name={`${isBefore ? "before" : "after"}-reading-yes-no-${idx}`}
            value={value}
            label={label}
            checked={
              (isBefore
                ? paddedBeforeReading[idx] === value
                : paddedAfterReading[idx] === value) || false
            }
            inputAriaProps={{
              "aria-label": `${isBefore ? "Before" : "After"} Reading "${fullLabel}"`,
            }}
            data-qa-selector={`${isBefore ? "before" : "after"}-anticipation-guide-radio-${fullLabel}`}
            onChange={(e) => {
              const selectedValue = e.target.value as "Y" | "N";
              const updated = [
                ...(isBefore ? paddedBeforeReading : paddedAfterReading),
              ];
              updated[idx] = selectedValue;
              handleChange(
                isBefore
                  ? { beforeReading: updated }
                  : { afterReading: updated },
              );
            }}
          />
        ))}
      </div>
    );
  };

  const checkIsOverflowingTextarea = (text: string = "") => {
    return text.length > INPUT_MAX_LENGTH / 2;
  };

  return (
    <div css={containerStyles}>
      <ExportablesTemplateHeaderSection
        templateTitle={title}
        directions={
          <OrganizerDirections
            name="Anticipation Guide"
            value={directions}
            handleChange={(e) => {
              handleChange({ directions: e.target.value });
            }}
            isStudent={isStudent}
          />
        }
        articleTitle={
          anticipationData?.article_title || anticipationData?.articleShortTitle
        }
      />
      <div css={guideRowsContainer}>
        {/* Header Row */}
        <div css={guideHeaderRow}>
          <div css={guideHeaderCellStatement}>Statement</div>
          <div css={guideHeaderCellYN}>
            <div>Before</div>
            <div>reading</div>
          </div>
          <div css={guideHeaderCellReason}>Reason</div>
          <div css={guideHeaderCellYN}>
            <div>After</div>
            <div>reading</div>
          </div>
          <div css={guideHeaderCellReason}>Reason</div>
        </div>
        {/* Data Rows */}
        {Array.from({ length: MAX_ROWS }).map((_, idx) => (
          <div css={guideRow} key={idx}>
            {/* Statement cell */}
            <div css={guideCellStatement}>
              {isStudent ? (
                <p
                  css={paragraphCss(
                    checkIsOverflowingTextarea(paddedStatements[idx]),
                  )}
                >
                  {paddedStatements[idx]?.substring(0, INPUT_MAX_LENGTH)}
                </p>
              ) : (
                <textarea
                  id={`statement-${idx}-${v4()}`}
                  css={textareaCss(
                    checkIsOverflowingTextarea(paddedStatements[idx]),
                  )}
                  value={paddedStatements[idx]?.substring(0, INPUT_MAX_LENGTH)}
                  maxLength={INPUT_MAX_LENGTH}
                  data-qa-selector={`exportables-statement-${idx}`}
                  onChange={(e) => {
                    const updated = [...paddedStatements];
                    updated[idx] = e.target.value;
                    handleChange({ statements: updated });
                  }}
                  aria-label={`Statement ${idx + 1}`}
                  rows={2}
                />
              )}
            </div>
            {ynCell(true, idx)}
            {/* Before reading Reason */}
            <div css={guideCellReason}>
              <textarea
                id={`before-reason-${idx}-${v4()}`}
                css={textareaCss(
                  checkIsOverflowingTextarea(paddedBeforeReasons[idx]),
                )}
                value={paddedBeforeReasons[idx]?.substring(0, INPUT_MAX_LENGTH)}
                maxLength={INPUT_MAX_LENGTH}
                data-qa-selector={`exportables-before-reading-${idx}`}
                onChange={(e) => {
                  const updated = [...paddedBeforeReasons];
                  updated[idx] = e.target.value;
                  handleChange({ beforeReasons: updated });
                }}
                aria-label={`Before reading reason ${idx + 1}`}
                rows={2}
              />
            </div>
            {ynCell(false, idx)}
            {/* After reading Reason */}
            <div css={guideCellReason}>
              <textarea
                id={`after-reason-${idx}-${v4()}`}
                css={textareaCss(
                  checkIsOverflowingTextarea(paddedAfterReasons[idx]),
                )}
                value={paddedAfterReasons[idx]?.substring(0, INPUT_MAX_LENGTH)}
                maxLength={INPUT_MAX_LENGTH}
                data-qa-selector={`exportables-after-reading-${idx}`}
                onChange={(e) => {
                  const updated = [...paddedAfterReasons];
                  updated[idx] = e.target.value;
                  handleChange({ afterReasons: updated });
                }}
                aria-label={`After reading reason ${idx + 1}`}
                rows={2}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnticipationGuideOrganizer;
